import {OrderRecipeComponentEditModel, OrderRecipeEditModel} from "@pages/orders/order.models";
import FlintTextBox from "@components/controls/FlintTextBox";
import {useTranslation} from "react-i18next";
import FlintNumericTextBox from "@components/controls/FlintNumericTextBox";
import {ComponentDataModel} from "@utils/Models";
import FlintIconButton from "@components/controls/FlintIconButton";
import {VivoPortal} from "@utils/AppConstants";
import EnumIconButtonDefaultType = VivoPortal.EnumIconButtonDefaultType;
import InkSeriesComponentPicker from "@components/page_components/dropdowns/InkSeriesComponentPicker";

export default function OrderRecipeComponentEdit(props: OrderRecipeComponentEditProps) {
    const {state, utils, handlers} = useOrderRecipeComponentEdit(props);
    const {t} = useTranslation();
    return (
        <div className={"order-recipe-component row py-2 align-items-start"}>
            <div className={"col-auto d-flex align-items-center text-secondary"}>
                #{utils.ordinal}
            </div>
            <div className={"col col-md-3 block-view"}>
                <InkSeriesComponentPicker
                    componentName={state.component.get.InkItemName}
                    placeHolder={t("COMMON_ComponentCode")}
                    disabled={props.Disabled || props.ComponentPickerDisabled}
                    seriesId={props.SeriesId}
                    ignoreSeriesFilter={props.IgnoreSeriesFilter}
                    maxLength={9}
                    validationData={{
                        validationMessage: utils.validator.formErrors?.[`Components[${props.ComponentIndex}].InkItemName`],
                        validateFunction: utils.validator.validateForm,
                        optionalObjectToValidate: state.recipe.get
                    }}
                    onItemSelected={c => handlers.handleOnComponentSelectedFromList(c)}/>
            </div>
            <div className={"col-12 col-md block-view"}>
                <FlintTextBox
                    label={t("COMMON_Description")}
                    disabled={props.Disabled}
                    value={state.component.get.InkItemDescription}
                    maxLength={200}
                    validationData={{
                        validationMessage: utils.validator.formErrors?.[`Components[${props.ComponentIndex}].InkItemDescription`],
                        validateFunction: utils.validator.validateForm,
                        optionalObjectToValidate: state.recipe.get
                    }}
                    onChange={x => handlers.handleOnComponentUpdated("InkItemDescription", x.value)}
                />
            </div>
            <div className={"col-4 col-md-2 block-view"}>
                <FlintNumericTextBox
                    min={0}
                    disabled={props.Disabled}
                    format={"n2"}
                    validationData={{
                        validationMessage: utils.validator.formErrors?.[`Components[${props.ComponentIndex}].Amount`],
                        validateFunction: utils.validator.validateForm,
                        optionalObjectToValidate: state.recipe.get
                    }}
                    label={t("lblAmount")}
                    onChange={x => handlers.handleOnComponentUpdated("Amount", x.value ?? 0)}
                    value={state.component.get.Amount}/>
            </div>
            <div className={"col-2 col-md-1"}>
                <div className={"text-end"}>{utils.percent.toFixed(2)}&nbsp;%</div>
            </div>
            <div className={"col-2 col-md-auto text-end"}>
                {!props.Disabled && props.Recipe.Components.length > 1 && (
                    <FlintIconButton iconEnum={EnumIconButtonDefaultType.Delete} themeColor={"error"} fillMode={"flat"}
                                     onClick={handlers.handleComponentDeleted}/>
                )}
            </div>
        </div>
    )
}

export interface OrderRecipeComponentEditProps {
    ComponentIndex: number;
    Recipe: OrderRecipeEditModel;
    TotalAmount: number;
    Disabled?: boolean;
    ComponentPickerDisabled?: boolean;
    SeriesId?: number;
    IgnoreSeriesFilter?: boolean;
    OnComponentDeleted: (index: number) => void;
    OnComponentUpdated: <T extends keyof OrderRecipeComponentEditModel>(index: number, key: T, value: OrderRecipeComponentEditModel[T]) => void;
    recipeValidator: {
        formErrors: any;
        validateForm: (objectToValidate?: any) => Promise<void>;
    };
}

function useOrderRecipeComponentEdit(props: OrderRecipeComponentEditProps) {
    function handleOnComponentUpdated<T extends keyof OrderRecipeComponentEditModel>(key: T, value: OrderRecipeComponentEditModel[T]) {
        props.OnComponentUpdated(props.ComponentIndex, key, value);
    }

    function handleOnComponentSelectedFromList(component: ComponentDataModel | null) {
        props.OnComponentUpdated(props.ComponentIndex, "InkItemGuid", component?.Guid ?? null);
        props.OnComponentUpdated(props.ComponentIndex, "InkItemName", component?.Name ?? "");
        props.OnComponentUpdated(props.ComponentIndex, "InkItemDescription", component?.Description ?? "");
    }
    const component = props.Recipe.Components[props.ComponentIndex];
    const percent = props.TotalAmount > 0 ? component.Amount / props.TotalAmount * 100 : 0;
    return {
        state: {
            recipe:{get:props.Recipe},
            component: {get: component},
        },
        utils: {
            ordinal: props.ComponentIndex + 1,
            percent: percent,
            validator: props.recipeValidator,
        },
        handlers: {
            handleComponentDeleted: () => props.OnComponentDeleted(props.ComponentIndex),
            handleOnComponentUpdated,
            handleOnComponentSelectedFromList,
        }
    }
}